"use client";

import { useEffect, useRef, useState } from "react";
import css from "./LiveThumbnailVideo.module.css";

export interface LiveThumbnailVideoProps {
  readonly src: string;
  /** Stable identity for the underlying photo — when this changes, the
   *  video element remounts and replays from the start. */
  readonly photoId: string;
  /** Accessible name for the live-photo badge. */
  readonly badgeLabel: string;
}

/**
 * Live Photo overlay for thumbnails. Plays muted, looped, in-viewport only —
 * IntersectionObserver pauses the video when scrolled out of sight to keep
 * a long masonry from running 50+ video decoders concurrently (Safari iOS
 * caps around 6 anyway). A small "LIVE" badge advertises the affordance
 * for the still frames (out of viewport, before video can play, or when
 * autoplay is denied).
 */
export function LiveThumbnailVideo({
  src,
  photoId,
  badgeLabel,
}: LiveThumbnailVideoProps) {
  const ref = useRef<HTMLVideoElement | null>(null);
  const [isPlaying, setPlaying] = useState(false);

  useEffect(() => {
    const video = ref.current;
    if (!video) return;
    if (typeof IntersectionObserver === "undefined") return;

    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          video.play().then(
            () => setPlaying(true),
            () => setPlaying(false),
          );
        } else {
          video.pause();
          setPlaying(false);
        }
      },
      // 10% intersect: start a touch before the thumbnail is fully on-screen
      // so the first frame is decoded by the time it's visible.
      { threshold: 0.1 },
    );
    io.observe(video);
    return () => io.disconnect();
  }, []);

  return (
    <>
      <video
        key={photoId}
        ref={ref}
        className={css.video}
        src={src}
        muted
        playsInline
        loop
        preload="metadata"
        aria-hidden
      />
      {!isPlaying ? <span className={css.badge}>{badgeLabel}</span> : null}
    </>
  );
}
